import { useCallback } from 'react';
import { cn } from '@scio3/ui';
import { analyticsOptibotMessage } from '@/lib/analytics-service';
import { useOptibotStore } from '../stores/optibot-store';
import { useOptiBot, setActiveImageIds } from '../hooks/useOptiBot';
import { usePageContext } from '../hooks/usePageContext';
import { useImageUpload } from '../hooks/useImageUpload';
import { OptiBotHeader } from './OptiBotHeader';
import { OptiBotMessages } from './OptiBotMessages';
import { OptiBotInput } from './OptiBotInput';

/**
 * Right-side panel for OptiBot chat.
 * Renders as a flex sidebar <aside> that pushes main content left when open.
 * Positioned below the TopNavigation (56px header) via the flex layout in AppShell.
 */
export function OptiBotPanel() {
  const { isOpen, isExpanded } = useOptibotStore();
  const {
    messages,
    sendMessage,
    isLoading,
    stop,
    input,
    setInput,
    handleApprove,
    handleDeny,
    handleFeedback,
    clearChat,
    loadConversation,
    getMessageTimestamp,
    isLoadingHistory,
    activeThreadId,
  } = useOptiBot();
  const { suggestions } = usePageContext();
  const imageUpload = useImageUpload();

  const handleSend = useCallback(
    async (text: string) => {
      // Set pending image IDs for the transport body (read by the body callback)
      const imageIds = imageUpload.getUploadedImageIds();
      if (imageIds.length > 0) {
        setActiveImageIds(imageIds);
      }

      // Collect FileUIParts for Claude vision (reads files as data URLs)
      const fileParts = imageUpload.hasImages ? await imageUpload.getFileUIParts() : undefined;

      analyticsOptibotMessage({ messageLength: text.length });

      sendMessage({
        text,
        ...(fileParts?.length ? { files: fileParts } : {}),
      });

      // Clear image previews after send
      imageUpload.clearAllImages();
    },
    [sendMessage, imageUpload],
  );

  return (
    <aside
      role="complementary"
      aria-label="OptiBot"
      className={cn(
        'shrink-0 overflow-hidden border-l border-border bg-background',
        'flex flex-col',
        'transition-[width] duration-300 ease-in-out',
        isOpen ? (isExpanded ? 'w-[min(80vw,800px)]' : 'w-[420px]') : 'w-0 border-l-0',
      )}
    >
      {isOpen && (
        <>
          <OptiBotHeader
            onNewChat={clearChat}
            onLoadConversation={loadConversation}
            activeThreadId={activeThreadId}
            hasMessages={messages.length > 0}
          />
          <OptiBotMessages
            messages={messages}
            isLoading={isLoading}
            isLoadingHistory={isLoadingHistory}
            onApprove={handleApprove}
            onDeny={handleDeny}
            onFeedback={handleFeedback}
            suggestions={suggestions}
            onSuggestionClick={handleSend}
            getMessageTimestamp={getMessageTimestamp}
          />
          <OptiBotInput
            input={input}
            setInput={setInput}
            onSend={handleSend}
            isLoading={isLoading}
            onStop={stop}
            images={imageUpload.images}
            isUploading={imageUpload.isUploading}
            fileInputRef={imageUpload.fileInputRef}
            acceptedExtensions={imageUpload.acceptedExtensions}
            onOpenFilePicker={imageUpload.openFilePicker}
            onFileSelect={imageUpload.handleFileSelect}
            onRemoveImage={imageUpload.removeImage}
          />
        </>
      )}
    </aside>
  );
}
